import React, {useEffect, useMemo, useRef, useState} from 'react';
import '../chat-css/TemplatesModal.css';
import Modal from '../../../../../components-new/ui/Modal';
import PropTypes from 'prop-types';
import Translate from '../../../../../components/Translate';
import config from "../../../../../config";
import {generalModalStyles} from "../../../../../utils/General";
import SilverButton from "../../../../../components-new/buttons/SilverButton";
import ChevronLeftIcon from "../../../../../assets/icons/ChevronLeftIcon";
import SearchInput from "../../../../../components-new/form/SearchInput";
import FolderOpenIcon from "../../../../../assets/icons/FolderOpenIcon";
import Template from "../../../../../components-new/general/Template";
import FolderIcon from "../../../../../assets/icons/FolderIcon";

Modal.setAppElement('#root');

/*
 * Picking a template.
 *
 * This is an answer the operator is about to send, so it is chosen the way the
 * suggestion list under the field is: with the keys. Arrows move, Enter takes,
 * Escape steps back out of a folder before it closes the window - between
 * reading the question and sending the answer the hand never leaves the
 * keyboard.
 *
 * The two levels are one view that slides rather than a grid swapped for a
 * list: opening a folder moves forward, the back button moves back, and the
 * movement itself says which of the two just happened.
 */
const modalStyles = {
    ...generalModalStyles.standardModalStyles,
    content: {
        ...generalModalStyles.standardModalStyles.content,
        width: '760px',
        maxWidth: 'calc(100vw - 32px)',
    },
};

const TemplatesModal = ({templates, isOpen, user, cancelAction, changeMessageTextToTemplate}) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedFolder, setSelectedFolder] = useState(null);
    /* the row the keys are on - the pointer sets it too, so the two never disagree */
    const [activeIndex, setActiveIndex] = useState(0);
    const [direction, setDirection] = useState('forward');

    const searchRef = useRef(null);
    const listRef = useRef(null);

    const folders = useMemo(() => {
        const grouped = templates.reduce((acc, template) => {
            if (!acc[template.folderName]) acc[template.folderName] = [];
            acc[template.folderName].push(template);
            return acc;
        }, {});

        const search = searchQuery.toLowerCase();

        return Object.keys(grouped)
            .filter(folderName =>
                folderName.toLowerCase().includes(search) ||
                grouped[folderName].some(template => template.text.toLowerCase().includes(search)))
            .map(folderName => ({name: folderName, count: grouped[folderName].length}));
    }, [templates, searchQuery]);

    /*
     * templates was missing from this list, so a template added while the
     * window was open was not in it until the folder was left and opened again.
     */
    const folderTemplates = useMemo(() =>
            templates
                .filter(template => template.folderName === selectedFolder
                    && template.text.toLowerCase().includes(searchQuery.toLowerCase()))
                .sort((a, b) => (b.id === null) - (a.id === null) || b.id - a.id),
        [templates, selectedFolder, searchQuery]);

    const inFolder = selectedFolder !== null;
    const rowCount = inFolder ? folderTemplates.length : folders.length;

    /* typing is what happens next, so the window opens on the field */
    useEffect(() => {
        if (isOpen && searchRef.current) searchRef.current.focus();
    }, [isOpen]);

    /* another set of rows is another first row */
    useEffect(() => {
        setActiveIndex(0);
    }, [selectedFolder, searchQuery]);

    useEffect(() => {
        if (!listRef.current) return;

        const row = listRef.current.querySelector('[data-row="' + activeIndex + '"]');
        if (row && row.scrollIntoView) row.scrollIntoView({block: 'nearest'});
    }, [activeIndex, selectedFolder, searchQuery]);

    const openFolder = (folderName) => {
        setDirection('forward');
        setSearchQuery('');
        setSelectedFolder(folderName);
        if (searchRef.current) searchRef.current.focus();
    };

    const leaveFolder = () => {
        setDirection('back');
        setSearchQuery('');
        setSelectedFolder(null);
        if (searchRef.current) searchRef.current.focus();
    };

    const pickTemplate = (text) => {
        changeMessageTextToTemplate(text);
        cancelAction();
    };

    /*
     * On the document, and in the capture phase.
     *
     * Hung on the window's own div first, which only ever saw a key if the
     * focus happened to be inside it - and the window opens with the focus on
     * the dialog that react-modal puts it on, a level above. The arrows did
     * nothing until something inside was clicked.
     *
     * Capture also puts this ahead of react-modal's own Escape, which is a
     * bubbling React handler: stopping the event here is what lets Escape mean
     * "out of this folder" before it means "close the window".
     */
    useEffect(() => {
        if (!isOpen) return undefined;

        document.addEventListener('keydown', handleKeyDown, true);
        return () => document.removeEventListener('keydown', handleKeyDown, true);
    });

    function handleKeyDown(event) {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            if (rowCount === 0) return;

            event.preventDefault();
            const step = event.key === 'ArrowDown' ? 1 : -1;
            setActiveIndex((index) => (index + step + rowCount) % rowCount);
            return;
        }

        if (event.key === 'Enter') {
            if (rowCount === 0) return;

            event.preventDefault();

            if (inFolder) {
                const template = folderTemplates[activeIndex];
                if (template) pickTemplate(template.text);
            } else {
                const folder = folders[activeIndex];
                if (folder) openFolder(folder.name);
            }
            return;
        }

        /*
         * Inside a folder the first Escape is a step back rather than a way
         * out: a wrong folder is opened far more often than an answer is
         * abandoned altogether.
         */
        if (event.key === 'Escape' && inFolder) {
            event.preventDefault();
            event.stopPropagation();
            leaveFolder();
        }
    }

    return (
        <Modal footer={null} closeTimeoutMS={config.closeTimeoutMS}
               isOpen={isOpen}
               style={modalStyles}
               onRequestClose={cancelAction}
               modalClassName="chat-templates"
               title={<Translate text="selectTemplate"/>}>
            <div className="chat-templates__body">

                <div className="chat-templates__toolbar">
                    <div className="chat-templates__crumbs">
                        <SilverButton
                            variant="ghost"
                            iconOnly
                            expandedHover
                            width="32px"
                            height="32px"
                            type="button"
                            disabled={!inFolder}
                            onClick={leaveFolder}
                        >
                            <ChevronLeftIcon width={18} height={18} stroke="currentColor"/>
                        </SilverButton>

                        {inFolder
                            ? <FolderOpenIcon width={20} height={20} stroke="currentColor"/>
                            : <FolderIcon width={20} height={20} stroke="currentColor"/>}

                        <span className="chat-templates__crumb-name">
                            {inFolder ? selectedFolder : <Translate text="templates"/>}
                        </span>

                        <span className="chat-templates__count">{rowCount}</span>
                    </div>

                    <SearchInput
                        ref={searchRef}
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        containerStyle={{width: '280px'}}
                        style={{height: '36px'}}
                    />
                </div>

                {/* the key remounts the view, which is what plays the slide */}
                <div
                    ref={listRef}
                    key={inFolder ? 'folder-' + selectedFolder : 'folders'}
                    className={'chat-templates__view chat-templates__view--' + direction}
                >
                    {inFolder ? (
                        <React.Fragment>
                            {folderTemplates.map((template, index) => (
                                <div
                                    key={template.id || template.text}
                                    data-row={index}
                                    className={'chat-templates__row' + (activeIndex === index ? ' is-active' : '')}
                                    onMouseEnter={() => setActiveIndex(index)}
                                >
                                    <Template
                                        text={template.text}
                                        readonly={true}
                                        onClick={() => pickTemplate(template.text)}
                                    />
                                </div>
                            ))}

                            {folderTemplates.length === 0 && (
                                <div className="chat-templates__empty"><Translate text="noTemplates"/></div>
                            )}
                        </React.Fragment>
                    ) : (
                        <div className="chat-templates__folders">
                            {folders.map((folder, index) => (
                                <div
                                    key={folder.name}
                                    data-row={index}
                                    className={'chat-templates__folder' + (activeIndex === index ? ' is-active' : '')}
                                    onMouseEnter={() => setActiveIndex(index)}
                                    onClick={() => openFolder(folder.name)}
                                >
                                    <FolderOpenIcon width={24} height={24} stroke="currentColor"/>
                                    <span className="chat-templates__folder-name">{folder.name}</span>
                                    <span className="chat-templates__folder-count">{folder.count}</span>
                                </div>
                            ))}

                            {folders.length === 0 && (
                                <div className="chat-templates__empty"><Translate text="noFolders"/></div>
                            )}
                        </div>
                    )}
                </div>

                <div className="chat-templates__hints">
                    <span className="chat-templates__hint">
                        <kbd>↑</kbd><kbd>↓</kbd><Translate text="templatesHintMove"/>
                    </span>
                    <span className="chat-templates__hint">
                        <kbd>↵</kbd>
                        <Translate text={inFolder ? 'templatesHintPick' : 'templatesHintOpen'}/>
                    </span>
                    <span className="chat-templates__hint">
                        <kbd>Esc</kbd>
                        <Translate text={inFolder ? 'templatesHintBack' : 'close'}/>
                    </span>
                </div>

            </div>
        </Modal>
    );
};

TemplatesModal.propTypes = {
    templates: PropTypes.array.isRequired,
    user: PropTypes.object.isRequired,
    cancelAction: PropTypes.func.isRequired,
    changeMessageTextToTemplate: PropTypes.func.isRequired,
};


export default TemplatesModal;



// WEBPACK FOOTER //
// ./src/scenes/dashboard/components/chat/chat-components/TemplatesModal.jsx